// src/pages/FormSubjectsStatusPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, CircularProgress, TextField, MenuItem
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";

import DatePicker from "react-multi-date-picker";
import DateObject from "react-date-object";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";

import { getFormById } from "../controllers/form.controller";
import { getAllProjects } from "../controllers/project.controller";
import { getClinicsByProject2 } from "../controllers/clinic.controller";
import { getFormSubjectsStatus } from "../controllers/formStatus.controller";

// ---------- helpers (JS Date) ----------
const toISOStartOfDay = (date) => {
  if (!date) return "";
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

const toISOEndOfDay = (date) => {
  if (!date) return "";
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
};

// برای نمایش در DatePicker (شمسی)
const toPickerValue = (date) => {
  if (!date) return null;
  return new DateObject({ date, calendar: persian, locale: persian_fa });
};

// خروجی DatePicker (DateObject) -> JS Date
const fromPickerValue = (val) => {
  if (!val) return null;
  // val معمولاً DateObject هست
  return val.toDate();
};

const FormSubjectsStatusPage = () => {
  const { formId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);

  // ✅ تاریخ‌های پیشفرض فقط با Date معمولی
  const defaultDates = useMemo(() => {
    const t = new Date();
    const a = new Date();
    a.setDate(t.getDate() - 14);
    return { today: t, fourteenDaysAgo: a };
  }, []);

  // لیست‌های فیلتر
  const [projects, setProjects] = useState([]);
  const [clinics, setClinics] = useState([]);

  // فیلترها (JS Date)
  const [filters, setFilters] = useState({
    search: "",
    projectId: "",
    clinicId: "",
    gender: "",
    responseStatus: "any", // any|pending|accepted|rejected|noResponse
    startDate: defaultDates.fourteenDaysAgo, // Date
    endDate: defaultDates.today,             // Date
  });

  // داده‌ها
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  // paging
  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [loading, setLoading] = useState(true);

  const loadClinics = async (projectId) => {
    try {
      if (!projectId) {
        setClinics([]);
        return;
      }
      const res = await getClinicsByProject2(projectId);
      setClinics(res || []);
    } catch (e) {
      console.error("loadClinics error:", e);
      setClinics([]);
    }
  };

  const fetchData = async (override = {}) => {
    setLoading(true);
    try {
      const f = { ...filters, ...override };

      const params = {
        search: f.search || "",
        projectId: f.projectId || "",
        clinicId: f.clinicId || "",
        gender: f.gender || "",
        responseStatus: f.responseStatus || "any",
        startDate: toISOStartOfDay(f.startDate),
        endDate: toISOEndOfDay(f.endDate),
        page: override.page ?? page,
        limit,
      };

      const res = await getFormSubjectsStatus(formId, params);
      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error("fetchData error:", e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = async () => {
    // ✅ هر بار تاریخ جدید بسازیم (نه reuse)
    const t = new Date();
    const a = new Date();
    a.setDate(t.getDate() - 14);

    const nf = {
      search: "",
      projectId: "",
      clinicId: "",
      gender: "",
      responseStatus: "any",
      startDate: a,
      endDate: t,
    };

    setFilters(nf);
    setClinics([]);
    setPage(1);

    // ✅ این fetch با nf انجام میشه => مشکل "دوبار کلیک" حل
    await fetchData({ ...nf, page: 1 });
  };

  useEffect(() => {
    (async () => {
      try {
        const f = await getFormById(formId);
        setForm(f);

        const ps = await getAllProjects();
        setProjects(ps || []);
      } catch (e) {
        console.error("init error:", e);
      }
    })();
  }, [formId]);

  // فقط وقتی صفحه عوض شد یا فرم عوض شد
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId, page, limit]);

  const handleApply = async () => {
    // ✅ اول صفحه ۱، بعد fetch با page=1
    setPage(1);
    await fetchData({ page: 1 });
  };

  const handleProjectChange = async (value) => {
    setFilters((p) => ({ ...p, projectId: value, clinicId: "" }));
    await loadClinics(value);
  };

  const getStatusLabel = (row) => {
    if (!row?.hasResponse) return "پاسخ داده نشده";
    if (row.evaluationStatus === "accepted") return "✅ تأیید شده";
    if (row.evaluationStatus === "rejected") return "❌ رد شده";
    if (row.evaluationStatus === "pending") return "⏳ در انتظار بررسی";
    return "پاسخ ثبت شده";
  };

  const handleFillForm = (subjectId) => {
    navigate(`/respond-form/${formId}?subjectId=${subjectId}`);
  };

  const headerProject = form?.projectTitle?.fa || form?.projectTitle?.en || "—";
  const headerClinic = form?.clinicTitle?.fa || form?.clinicTitle?.en || "—";
  const headerPage = form?.pageTitle?.fa || form?.pageTitle?.en || "—";
  const headerForm = form?.title?.fa || form?.title || "فرم بدون عنوان";

  return (
    <Box sx={{ p: 3 }} dir="rtl">
      <Typography variant="h5" mb={1} fontWeight="bold">
        وضعیت بیماران برای فرم: {headerForm}
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2}>
        پروژه: {headerProject} | کلینیک: {headerClinic} | صفحه: {headerPage}
      </Typography>

      {/* فیلترها */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
          <TextField
            size="small"
            label="جستجو (نام/نام‌خانوادگی/کدملی)"
            value={filters.search}
            onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
          />

          <TextField
            select
            size="small"
            label="پروژه"
            value={filters.projectId}
            onChange={(e) => handleProjectChange(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">همه</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p._id} value={p._id}>
                {p.title?.fa || p.title?.en || "پروژه"}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="کلینیک"
            value={filters.clinicId}
            onChange={(e) => setFilters((p) => ({ ...p, clinicId: e.target.value }))}
            sx={{ minWidth: 180 }}
            disabled={!filters.projectId}
          >
            <MenuItem value="">همه</MenuItem>
            {clinics.map((c) => (
              <MenuItem key={c._id} value={c._id}>
                {c.title?.fa || c.title?.en || "کلینیک"}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="جنسیت"
            value={filters.gender}
            onChange={(e) => setFilters((p) => ({ ...p, gender: e.target.value }))}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">همه</MenuItem>
            <MenuItem value="male">مذکر</MenuItem>
            <MenuItem value="female">مونث</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="وضعیت پاسخ"
            value={filters.responseStatus}
            onChange={(e) => setFilters((p) => ({ ...p, responseStatus: e.target.value }))}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="any">همه</MenuItem>
            <MenuItem value="noResponse">پاسخ داده نشده</MenuItem>
            <MenuItem value="pending">در انتظار بررسی</MenuItem>
            <MenuItem value="accepted">تأیید شده</MenuItem>
            <MenuItem value="rejected">رد شده</MenuItem>
          </TextField>

          {/* تاریخ شمسی (ولی state: Date معمولی) */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
            <Box>
              <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                از تاریخ (ایجاد بیمار)
              </Typography>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                format="YYYY/MM/DD"
                value={toPickerValue(filters.startDate)}
                onChange={(val) => setFilters((p) => ({ ...p, startDate: fromPickerValue(val) }))}
                style={{ height: 40, width: 170 }}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
                تا تاریخ (ایجاد بیمار)
              </Typography>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                format="YYYY/MM/DD"
                value={toPickerValue(filters.endDate)}
                onChange={(val) => setFilters((p) => ({ ...p, endDate: fromPickerValue(val) }))}
                style={{ height: 40, width: 170 }}
              />
            </Box>
          </Box>

          <Button variant="contained" onClick={handleApply}>
            اعمال فیلتر
          </Button>

          <Button variant="outlined" color="inherit" onClick={resetFilters}>
            پاک کردن فیلتر
          </Button>
        </Box>
      </Paper>

      {/* جدول */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>بیمار</TableCell>
                <TableCell>کد ملی</TableCell>
                <TableCell>پروژه</TableCell>
                <TableCell>کلینیک</TableCell>
                <TableCell>آخرین زمان پاسخ</TableCell>
                <TableCell>وضعیت ارزیابی</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((s) => {
                const submitted = s.submittedAt
                  ? new Date(s.submittedAt).toLocaleString("fa-IR")
                  : "—";

                let buttonLabel = "پاسخ‌دهی";
                let buttonColor = "success";

                if (s.hasResponse) {
                  if (s.evaluationStatus === "accepted") {
                    buttonLabel = "مشاهده";
                    buttonColor = "secondary";
                  } else {
                    buttonLabel = "ویرایش پاسخ";
                    buttonColor = "warning";
                  }
                }

                return (
                  <TableRow key={s._id}>
                    <TableCell>
                      {(`${s.firstName || ""} ${s.lastName || ""}`).trim() || s.nationalId || "بیمار"}
                    </TableCell>
                    <TableCell>{s.nationalId || "—"}</TableCell>
                    <TableCell>{s.projectTitle?.fa || s.projectTitle?.en || "—"}</TableCell>
                    <TableCell>{s.clinicTitle?.fa || s.clinicTitle?.en || "—"}</TableCell>
                    <TableCell>{submitted}</TableCell>
                    <TableCell>{getStatusLabel(s)}</TableCell>
                    <TableCell align="center">
                      <Button
                        variant="contained"
                        color={buttonColor}
                        size="small"
                        onClick={() => handleFillForm(s._id)}
                      >
                        {buttonLabel}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}

              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    هیچ موردی یافت نشد.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* paging */}
          {total > limit && (
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, p: 2 }}>
              <Button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                صفحه قبل
              </Button>
              <Typography variant="body2">
                صفحه {page} از {Math.ceil(total / limit)}
              </Typography>
              <Button
                disabled={page >= Math.ceil(total / limit)}
                onClick={() => setPage((p) => p + 1)}
              >
                صفحه بعد
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default FormSubjectsStatusPage;
